import React from 'react';
import {
    Box,
    Container,
    Typography,
    Link,
    Chip,
} from '@mui/material';
import logo from '../Assests/images/logo.svg';
import Footerimg from '../Assests/footer.png';
import { useNavigate } from 'react-router-dom';

const Footer = ({ textColor = '#000000', logoTextColor = '#000000', headingColor = '#2196F3' }) => {
    const navigate = useNavigate();

    const footerSections = [
        {
            title: 'Product',
            items: [
                { label: 'Payment gateway', href: '#payment-gateway' },
                { label: 'Custom Payments', href: '#custom-payments' },
                { label: 'API & Payment Integrations', href: '#api-integrations' },
                { label: 'Payment Checkouts', href: '#payment-checkouts' },
                {
                    label: 'UPI & Recharge App',
                    href: '#upi-recharge',
                    chip: 'Coming Soon',
                    chipColor: '#2196F3'
                }
            ]
        },
        {
            title: 'Company',
            items: [
                { label: 'About us', href: '/about-us' },
                { label: 'Board of Directors', href: '/board-of-directors' },
                { label: 'Blogs & News', href: '/blogs' },
                { label: 'Careers', href: '/careers' },
                { label: 'Contact us', href: '/contact' }
            ]
        },
        {
            title: 'Others',
            items: [
                { label: 'Terms & Conditions', href: '#terms' },
                { label: 'merchandise Agreement', href: '#merchandise' },
                { label: 'Privacy policy', href: '#privacy' },
                { label: 'Fraud Alert', href: '#fraud-alert' },
                { label: 'Onboard Requirements', href: '#onboard-requirements' },
                { label: 'Refund Policy', href: '#refund' }
            ]
        }
    ];

    const handleLogoClick = () => {
        navigate('/');
    };

    return (
        <Box
            component="footer"
            sx={{
                position: 'relative',
                py: 6,
                boxSizing: 'border-box',
                px: { xs: 0, md: 8 },
                mt: 8,
                
                overflow: 'hidden',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: { xs: '100%', md: '100%' },
                  height: '100%',
                  backgroundImage: `url(${Footerimg})`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right bottom',
                  zIndex: 0,
                  opacity: 0.9,
                },
            }}
        >
            <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                {/* Main Footer Content */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 6,
                        mb: 4,
                        flexWrap: { xs: 'wrap', md: 'nowrap' }
                    }}
                >
                    {/* Left Section - Company Info with Logo and Text */}
                    <Box sx={{ flex: '0 0 auto', maxWidth: '400px' }}>
                        {/* Logo and Text Section */}
                        <Box
                            onClick={handleLogoClick}
                            sx={{
                                mb: 3,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                cursor: 'pointer',
                            }}
                        >
                            <Box
                                component="img"
                                src={logo}
                                alt="ZutoPay Logo"
                                sx={{
                                    width: "45px",
                                    height: "auto",
                                    display: 'block',
                                }}
                            />
                            <Typography
                                sx={{
                                    color: logoTextColor, // Use prop for logo text color
                                    fontSize: '3rem',
                                    fontWeight: 700,
                                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                                    letterSpacing: '-1px',
                                    fontStyle: 'italic',
                                    textTransform: 'lowercase',
                                    lineHeight: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                zutopay
                            </Typography>
                        </Box>

                        <Typography
                            sx={{
                                fontSize: '0.95rem',
                                lineHeight: 1.7,
                                color: textColor, // Use prop for text color
                                maxWidth: '400px',
                            }}
                        >
                            Zutopay is a secure and reliable mobile payment solution
                            designed to make transactions effortless. We help users pay,
                            send, and receive money instantly with complete peace of mind.
                            Every payment is protected with advanced encryption and real-time
                            verification. Whether shopping online or managing your business,
                            Zutopay ensures speed, safety, and simplicity in every transaction.
                        </Typography>
                    </Box>

                    {/* Right Section - Footer Links */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 4,
                            flex: '1 1 auto',
                            maxWidth: '700px',
                            width: '100%'
                        }}
                    >
                        {footerSections.map((section, sectionIndex) => (
                            <Box key={section.title} sx={{ flex: '1 1 auto', minWidth: '180px' }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontSize: '1.5rem',
                                        fontWeight: 600,
                                        color: headingColor, // Use prop for heading color
                                        mb: 2.5,
                                    }}
                                >
                                    {section.title}
                                </Typography>
                                <Box>
                                    {section.items.map((item, itemIndex) => (
                                        <Box
                                            key={item.label}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                mb: 1.5
                                            }}
                                        >
                                            <Link
                                                href={item.href}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    navigate(item.href);
                                                }}
                                                sx={{
                                                    color: textColor, // Use prop for text color
                                                    textDecoration: 'none',
                                                    fontSize: '0.9rem',
                                                    lineHeight: 1.6,
                                                    '&:hover': {
                                                        color: headingColor, // Use prop for hover color
                                                        textDecoration: 'none',
                                                    },
                                                    transition: 'color 0.3s ease',
                                                }}
                                            >
                                                {item.label}
                                            </Link>
                                            {item.chip && (
                                                <Chip
                                                    label={item.chip}
                                                    size="small"
                                                    sx={{
                                                        ml: 1,
                                                        backgroundColor: item.chipColor,
                                                        color: '#ffffff',
                                                        fontSize: '0.7rem',
                                                        height: '20px',
                                                        '& .MuiChip-label': {
                                                            px: 1,
                                                        },
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Copyright Section */}
                <Box
                    sx={{
                        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                        pt: 3,
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '0.9rem',
                            color: textColor, // Use prop for copyright text color
                            fontWeight: 400,
                        }}
                    >
                        Copyright © 2025 Zutopay. All Rights Reserved.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default Footer;